import {
  hidePersonNameDialog,
  personNameDialogSaved,
} from '../store/actions/personNameDialog.actions';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid, Typography } from '@material-ui/core';
import RMSCommonDialog from '../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../shared/components/dialog';
import { RMSTextField, RMSSelect } from '../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../shared/components/modules/RMSRecordHistoryButton';
import CreateFormResolver from '../../shared/utility/createFormResolver';
import { executeApiAction } from '../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../shared/utility/useConfirmDialog';
import personNameSchema from '../schema/personNameSchema';
import { DEFAULT_VALUES } from '../constants/personNameFormConstants';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';
import useUserDictionaryOptions from '../hooks/useUserDictionaryOptions';
import { createPersonName, editPersonName, getPersonName } from '../services/personNameService';
import { buildFullName } from '../utility/personNameHelper';

const AddEditNameDialog = ({ dialog }) => {
  const { personId, nameId = null, nameChangeSnapshot = null, requestId } = dialog;
  const open = true;
  const dispatch = useDispatch();
  const onClose = useCallback(() => dispatch(hidePersonNameDialog(requestId)), [
    dispatch,
    requestId,
  ]);

  const [name, setName] = useState(null);
  const [loading, setLoading] = useState(Boolean(nameId));
  const canEdit = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;
  const canSave = canEdit && !loading && (!nameId || name?.ptsNameID === nameId);
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const isNameChangeMode = Boolean(nameChangeSnapshot);
  const isEditMode = Boolean(nameId);

  const methods = useForm({
    resolver: CreateFormResolver(personNameSchema),
    mode: 'onSubmit',
    disabled: !canSave,
    defaultValues: { ...DEFAULT_VALUES, ...(nameChangeSnapshot || name || {}) },
  });

  const { reset } = methods;

  const onSaved = useCallback(
    (name) => {
      dispatch(personNameDialogSaved(name, requestId));
      dispatch(hidePersonNameDialog(requestId));
    },
    [dispatch, requestId]
  );

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const { prefixOptions, suffixOptions, nameTypeOptions } = useUserDictionaryOptions();
  const watchedNameFields = useWatch({
    control: methods.control,
    name: ['Prefix', 'FirstName', 'MiddleName', 'LastName', 'Suffix'],
  });
  const [Prefix, FirstName, MiddleName, LastName, Suffix] = watchedNameFields;
  const livePreview = buildFullName({ Prefix, FirstName, MiddleName, LastName, Suffix });

  useEffect(() => {
    let active = true;
    setName(null);
    reset({ ...DEFAULT_VALUES, ...(nameChangeSnapshot || {}) });
    if (!nameId) {
      setLoading(false);
      return;
    }
    executeApiAction({
      dispatch,
      setLoading: (value) => {
        if (active) setLoading(value);
      },
      action: () => getPersonName(nameId),
      onSuccess: (record) => {
        if (!active || !record?.ptsNameID) return;
        reset({ ...DEFAULT_VALUES, ...record });
        setName(record);
      },
    });
    return () => {
      active = false;
    };
  }, [nameId, nameChangeSnapshot, dispatch, reset]);

  const onSubmit = useCallback(
    async (data) => {
      if (!canSave) return;
      const payload = {
        ptsPersonID: name?.ptsPersonID ?? personId,
        FirstName: data.FirstName || null,
        MiddleName: data.MiddleName || null,
        LastName: data.LastName || null,
        Prefix: data.Prefix || null,
        Suffix: data.Suffix || null,
        NickName: data.NickName || null,
        Notes: data.Notes || null,
        NameType: data.NameType || null,
        IsDefault: false,
        IsActive: true,
        FullName: buildFullName(data),
        UpdatedBy: currentUser?.Username,
      };

      if (!isEditMode) payload.CreatedBy = currentUser?.Username;

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => (isEditMode ? editPersonName(nameId, payload) : createPersonName(payload)),
        onSuccess: (savedName) => {
          dispatch(
            notify(
              isEditMode ? 'Name updated successfully' : 'Name created successfully',
              'success'
            )
          );
          onSaved?.(savedName);
        },
      });
    },
    [isEditMode, name, nameId, personId, currentUser, dispatch, onSaved, canSave]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this name. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={isNameChangeMode ? 'Save Previous Name' : isEditMode ? 'Edit Name' : 'Add Name'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting || loading}
      saveDisabled={submitting || !canSave}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!name?.Created}
            created={name?.Created}
            createdBy={name?.CreatedBy}
            updated={name?.Updated}
            updatedBy={name?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        {isNameChangeMode ? (
          <Box mb={2}>
            <Typography variant="body2">
              Previous name for this person: <strong>{buildFullName(nameChangeSnapshot)}</strong>{' '}
              will be saved. Select a Type for this previous name.
            </Typography>
          </Box>
        ) : (
          livePreview && (
            <Box mb={2}>
              <Typography variant="h6">{livePreview}</Typography>
            </Box>
          )
        )}

        <Grid container spacing={2}>
          {!isNameChangeMode && (
            <>
              <Grid item xs={4}>
                <RMSSelect
                  name="Prefix"
                  label="Prefix"
                  options={prefixOptions}
                  {...getDictionarySelectProps()}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField name="FirstName" label="First Name" inputProps={{ maxLength: 100 }} />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField
                  name="MiddleName"
                  label="Middle Name"
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField name="LastName" label="Last Name" inputProps={{ maxLength: 100 }} />
              </Grid>
              <Grid item xs={4}>
                <RMSSelect
                  name="Suffix"
                  label="Suffix"
                  options={suffixOptions}
                  {...getDictionarySelectProps()}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField name="NickName" label="Nick Name" inputProps={{ maxLength: 20 }} />
              </Grid>
            </>
          )}

          <Grid item xs={isNameChangeMode ? 12 : 4}>
            <RMSSelect
              name="NameType"
              label="Name Type"
              options={nameTypeOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>

          <Grid item xs={12}>
            <RMSTextField
              name="Notes"
              label="Notes"
              multiline
              rows={4}
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>
        </Grid>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default AddEditNameDialog;
